/** Bridge details country name → default phone dial code */
export const BRIDGE_COUNTRY_TO_DIAL_CODE = {
  USA: '1',
  Canada: '1',
  India: '91',
  UK: '44',
  Australia: '61',
};

export const COUNTRY_CODES = [
  { value: '1', label: 'United States', iso: 'us' },
  { value: '1', label: 'Canada', iso: 'ca', selectValue: 'ca' },
  { value: '91', label: 'India', iso: 'in' },
  { value: '44', label: 'United Kingdom', iso: 'gb' },
  { value: '61', label: 'Australia', iso: 'au' },
];

export const normalizeCode = (code) => String(code || '').replace(/^\+/, '').trim();

export const getDialCodeForBridgeCountry = (bridgeCountry) =>
  BRIDGE_COUNTRY_TO_DIAL_CODE[bridgeCountry] || '1';

export const getCountryCodeOption = (dialCode) => {
  const code = normalizeCode(dialCode);
  return COUNTRY_CODES.find((c) => c.value === code) || COUNTRY_CODES[0];
};

/**
 * ISO code for the SVG flag.
 * +1 is shared by USA and Canada. An explicit Canada/USA choice wins.
 * Otherwise the company country decides the flag when it is one of those two,
 * then the bridge country.
 */
export const getDisplayFlagIso = (dialCode, bridgeCountry, companyCountry, explicitIso) => {
  const code = normalizeCode(dialCode);
  const chosen = String(explicitIso || '').toLowerCase();
  if (code === '1' && (chosen === 'ca' || chosen === 'us')) return chosen;
  if (code === '1') {
    const sharedCountry = companyCountry === 'Canada' || companyCountry === 'USA'
      ? companyCountry
      : bridgeCountry;
    if (sharedCountry === 'Canada') return 'ca';
    return 'us';
  }
  return getCountryCodeOption(code).iso || 'us';
};

export const PHONE_RULES_BY_CODE = {
  1: { length: 10, label: 'US/Canada' },
  91: { length: 10, label: 'India' },
  44: { length: 10, label: 'UK' },
  61: { length: 9, label: 'Australia' },
};

export const getPhoneRule = (countryCode) => {
  const code = normalizeCode(countryCode);
  return PHONE_RULES_BY_CODE[code] || PHONE_RULES_BY_CODE[Number(code)] || { length: 10, label: 'selected country' };
};

export const sanitizePhoneInput = (phone, countryCode) => {
  const max = getPhoneRule(countryCode).length;
  return String(phone || '').replace(/\D/g, '').slice(0, max);
};

export const validatePhoneNumber = (person, label) => {
  const digits = sanitizePhoneInput(person.phone, person.countryCode);
  const rule = getPhoneRule(person.countryCode);
  if (!digits) return `${label}: phone number is required.`;
  if (digits.length !== rule.length) {
    return `${label}: enter exactly ${rule.length} digits for ${rule.label} (+${normalizeCode(person.countryCode)}).`;
  }
  return null;
};

export const emptyPerson = (dialCode = '91') => ({
  name: '',
  email: '',
  countryCode: normalizeCode(dialCode),
  phone: '',
});

/**
 * User.phone is often stored as E.164 (e.g. +15555555556) after company/bridge
 * provisioning. Staff form fields need dial code + national number separately.
 */
export const splitPhoneWithDialCode = (rawPhone, fallbackDialCode = '1') => {
  const fallback = normalizeCode(fallbackDialCode) || '1';
  const digits = String(rawPhone || '').replace(/\D/g, '');
  if (!digits) {
    return { countryCode: fallback, phone: '' };
  }

  const knownCodes = [...COUNTRY_CODES.map((c) => c.value)].sort((a, b) => b.length - a.length);
  for (const code of knownCodes) {
    if (!digits.startsWith(code)) continue;
    const national = digits.slice(code.length);
    const rule = getPhoneRule(code);
    if (national.length === rule.length) {
      return { countryCode: code, phone: national };
    }
  }

  return {
    countryCode: fallback,
    phone: sanitizePhoneInput(digits, fallback),
  };
};

/** Map the logged-in user onto a staff person row (name, email, phone). */
export const personFromLoggedInUser = (user, fallbackDialCode = '1', bridges = []) => {
  const userCode = normalizeCode(user?.countryCode || user?.dialCode || user?.country_code);
  const rawPhone = user?.phone || user?.mobile || user?.phoneNumber || user?.mobileNumber || '';
  const split = splitPhoneWithDialCode(rawPhone, userCode || fallbackDialCode);
  const fromAuth = {
    name: String(user?.name || '').trim(),
    email: String(user?.email || '').trim(),
    countryCode: userCode || split.countryCode,
    phone: split.phone,
  };

  if (fromAuth.name && fromAuth.email && fromAuth.phone) return fromAuth;

  const email = fromAuth.email.toLowerCase();
  const userId = String(user?._id || user?.id || '');
  for (const bridge of bridges || []) {
    for (const admin of bridge.admins || []) {
      const adminEmail = String(admin?.email || admin?.user?.email || '').trim().toLowerCase();
      const adminUserId = String(
        admin?.user?._id || (typeof admin?.user === 'object' ? admin?.user?.id : admin?.user) || ''
      );
      const matches =
        (email && adminEmail === email) ||
        (userId && adminUserId && userId === adminUserId);
      if (!matches) continue;

      const adminCode = normalizeCode(admin?.countryCode) || fromAuth.countryCode;
      const adminPhone =
        fromAuth.phone ||
        splitPhoneWithDialCode(admin?.phone, adminCode).phone ||
        sanitizePhoneInput(admin?.phone, adminCode);
      return {
        name: fromAuth.name || String(admin?.name || admin?.user?.name || '').trim(),
        email: fromAuth.email || String(admin?.email || admin?.user?.email || '').trim(),
        countryCode: adminCode,
        phone: adminPhone,
      };
    }
  }

  return fromAuth;
};

/** National digits for a dial code. Accepts a local number or a +code number. */
export const nationalPhoneForCode = (rawPhone, countryCode) => {
  const code = normalizeCode(countryCode) || '1';
  const digits = String(rawPhone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith(code)) {
    const national = digits.slice(code.length);
    if (national.length === getPhoneRule(code).length) return national;
  }
  return sanitizePhoneInput(digits, code);
};

/** Company bridge-admin row (name, email, dial code, phone) after create or edit. */
export const personFromCompanyAdmin = (admin, fallbackDialCode = '1') => {
  if (!admin) return null;
  const countryCode = normalizeCode(admin.countryCode)
    || normalizeCode(fallbackDialCode)
    || '1';
  const name = String(admin.name || admin.user?.name || '').trim();
  const email = String(admin.email || admin.user?.email || '').trim();
  const phone = nationalPhoneForCode(admin.phone, countryCode);
  if (!name && !email && !phone) return null;
  return { name, email, countryCode, phone };
};

/**
 * Unfilled staff rows follow the company country dial code.
 * The locked first admin and anyone already filled are left as they are.
 */
export const applyCompanyDialToEmptyStaff = (staff, companyCountry) => {
  const dialCode = getDialCodeForBridgeCountry(companyCountry);
  if (!dialCode || !staff) return staff;
  const mapEmpty = (person) => {
    if (!isRowEmpty(person) || normalizeCode(person?.countryCode) === dialCode) return person;
    return {
      ...person,
      countryCode: dialCode,
      phone: sanitizePhoneInput(person?.phone, dialCode),
    };
  };
  return {
    ...staff,
    owners: (staff.owners || []).map(mapEmpty),
    admins: (staff.admins || []).map((person, idx) => (idx === 0 ? person : mapEmpty(person))),
    managers: (staff.managers || []).map((manager) => ({
      ...mapEmpty(manager),
      engineers: (manager.engineers || []).map(mapEmpty),
    })),
  };
};

/** Locked first Bridge Admin shows the assigned company admin; other rows stay as they are. */
export const applyLockedFirstAdmin = (staff, person) => {
  if (!person) return staff;
  const admins = staff?.admins?.length ? [...staff.admins] : [person];
  admins[0] = {
    name: person.name || '',
    email: person.email || '',
    countryCode: normalizeCode(person.countryCode) || admins[0]?.countryCode || '1',
    phone: person.phone || '',
  };
  return { ...staff, admins };
};

/**
 * Company country change: move admin dial codes that still follow the previous
 * country. A code chosen independently of the company country is left as-is.
 */
export const applyCompanyCountryToAdmins = (admins, previousCountry, nextCountry) => {
  const previousDial = getDialCodeForBridgeCountry(previousCountry);
  const nextDial = getDialCodeForBridgeCountry(nextCountry);
  return (admins || []).map((person) => {
    const current = normalizeCode(person?.countryCode);
    const followsCompany = !current || current === previousDial || isRowEmpty(person);
    if (!followsCompany || current === nextDial) return person;
    return {
      ...person,
      countryCode: nextDial,
      phone: sanitizePhoneInput(person.phone, nextDial),
    };
  });
};

export const emptyManager = (dialCode = '91') => ({
  ...emptyPerson(dialCode),
  engineers: [],
});

export const emptyStaffForBridge = (bridgeCountry, loggedInUser, bridges) => {
  const dialCode = getDialCodeForBridgeCountry(bridgeCountry);
  const firstAdmin =
    loggedInUser?.role === 'bridge_admin'
      ? personFromLoggedInUser(loggedInUser, dialCode, bridges)
      : emptyPerson(dialCode);
  return {
    owners: [emptyPerson(dialCode)],
    admins: [firstAdmin],
    managers: [emptyManager(dialCode)],
  };
};

const isRowEmpty = (p) =>
  !p?.name?.trim() && !p?.email?.trim() && !p?.phone?.trim();

/** Default dial code on empty staff rows from bridge country (step 1). */
export const applyBridgeCountryToStaff = (staff, bridgeCountry) => {
  const dialCode = getDialCodeForBridgeCountry(bridgeCountry);
  const mapPerson = (p) => {
    if (!isRowEmpty(p)) return p;
    return {
      ...p,
      countryCode: dialCode,
      phone: sanitizePhoneInput(p.phone, dialCode),
    };
  };
  return {
    owners: staff.owners.map(mapPerson),
    admins: staff.admins.map(mapPerson),
    managers: staff.managers.map((m) => ({
      ...mapPerson(m),
      engineers: (m.engineers || []).map(mapPerson),
    })),
  };
};

export const isRowFilled = (p) =>
  Boolean(p.name?.trim() || p.email?.trim() || p.phone?.trim());

export const personToPayload = (p) => ({
  name: p.name.trim(),
  email: p.email.trim().toLowerCase(),
  countryCode: normalizeCode(p.countryCode),
  phone: sanitizePhoneInput(p.phone, p.countryCode),
});

export const isPersonComplete = (p, { emailRequired = true } = {}) =>
  Boolean(
    p.name?.trim() &&
    normalizeCode(p.countryCode) &&
    p.phone?.trim() &&
    (!emailRequired || p.email?.trim())
  );

export const filterPeople = (list) =>
  list.filter(isRowFilled).map(personToPayload);

export const filterManagers = (managers) =>
  managers
    .filter(isRowFilled)
    .map((m) => ({
      ...personToPayload(m),
      engineers: (m.engineers || []).filter(isRowFilled).map(personToPayload),
    }));

const MANDATORY_STAFF_MESSAGE = 'At least one Bridge Admin is required.';

const requireMandatoryStaff = (admins) => {
  if (!admins.some(isRowFilled)) return MANDATORY_STAFF_MESSAGE;
  return null;
};

/** Validate staff; returns error string or null */
export function validateBridgeStaff(owners, admins, managers) {
  let err = requireMandatoryStaff(admins);
  if (err) return err;

  const checkList = (list, label, max, { emailRequired = true } = {}) => {
    const filled = list.filter(isRowFilled);
    if (filled.length > max) return `Maximum ${max} ${label} allowed.`;
    for (const p of filled) {
      if (!isPersonComplete(p, { emailRequired })) {
        return emailRequired
          ? `${label}: name, email, country code, and phone are required for each person.`
          : `${label}: name, country code, and phone are required for each person.`;
      }
      if (p.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email.trim())) {
        return `${label}: invalid email "${p.email}".`;
      }
      const phoneErr = validatePhoneNumber(p, label);
      if (phoneErr) return phoneErr;
    }
    return null;
  };

  err =
    checkList(owners, 'Bridge Owner', 3, { emailRequired: false }) ||
    checkList(admins, 'Bridge Admin', 3) ||
    checkList(managers, 'Bridge Manager', 6);
  if (err) return err;

  for (let i = 0; i < managers.length; i++) {
    const m = managers[i];
    if (!isRowFilled(m)) continue;
    const eng = (m.engineers || []).filter(isRowFilled);
    if (eng.length > 3) {
      return `Bridge Manager ${i + 1}: maximum 3 engineers allowed.`;
    }
    for (const e of eng) {
      if (!isPersonComplete(e)) {
        return `Bridge Engineer (under manager ${i + 1}): all fields are required.`;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.email.trim())) {
        return `Bridge Engineer: invalid email "${e.email}".`;
      }
      const phoneErr = validatePhoneNumber(e, `Bridge Engineer (under manager ${i + 1})`);
      if (phoneErr) return phoneErr;
    }
  }

  const emails = [];
  const collect = (list, role) => {
    for (const p of list.filter(isRowFilled)) {
      const em = (p.email || '').trim().toLowerCase();
      if (!em) continue;
      if (emails.includes(em)) {
        return `Duplicate email "${p.email}" — emails must be unique across all bridge staff.`;
      }
      emails.push(em);
    }
    return null;
  };

  err =
    collect(owners, 'owner') ||
    collect(admins, 'admin') ||
    collect(managers, 'manager');
  if (err) return err;

  for (const m of managers.filter(isRowFilled)) {
    err = collect(m.engineers || [], 'engineer');
    if (err) return err;
  }

  return null;
}

export const mapPersonFromApi = (p) => ({
  name: p?.name || p?.user?.name || '',
  email: p?.email || p?.user?.email || '',
  countryCode: normalizeCode(p?.countryCode) || '91',
  phone: p?.phone || '',
});

export const staffFromBridge = (bridge) => ({
  owners: (bridge?.owners?.length ? bridge.owners : [emptyPerson()]).map(mapPersonFromApi),
  admins: (bridge?.admins?.length ? bridge.admins : [emptyPerson()]).map(mapPersonFromApi),
  managers: (bridge?.managers?.length ? bridge.managers : [emptyManager()]).map((m) => ({
    ...mapPersonFromApi(m),
    engineers: (m.engineers || []).map(mapPersonFromApi),
  })),
});
